import { webPortalApi } from '#config/web-portal-api';
import { snackBarError } from '#action/fetch';
import {
  USER_NOTIFICATION_FETCH_STARTED,
  USER_NOTIFICATION_FETCH_SUCCEEDED,
  USER_NOTIFICATION_READ,
  USER_NOTIFICATION_DELETE,
  USER_NOTIFICATION_FETCH_FAILED,
  USER_NOTIFICATION_CLEAR,
  USER_NOTIFICATION_COUNT_FETCH_STARTED,
  USER_NOTIFICATION_COUNT_FETCH_SUCCEEDED
} from '#action/types';
import { getRequestOptions } from '#helper';

let abortController = new AbortController();

export const fetchUserNotification = (userId, paramOffset) => async (dispatch, getState) => {
  try {
    abortController.abort();

    dispatch({
      type: USER_NOTIFICATION_FETCH_STARTED
    });

    const { limit, offset } = getState().userNotification;
    const searchParams = new URLSearchParams({ offset: paramOffset ?? offset, limit });

    const url = webPortalApi.buildApiUrl(`user/${userId}/notification?${searchParams.toString()}`);

    abortController = new AbortController();
    const { signal } = abortController;
    // Debounce to do not send multiple requests in short time
    await new Promise(res => {
      setTimeout(() => {
        res();
      }, 200);
    });
    const res = await fetch(url, { ...getRequestOptions(), signal });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }

    if (!res.ok) {
      dispatch(
        snackBarError({
          message: 'Error in request'
        })
      );
    }

    const notificationRes = await res.json() ?? {};

    dispatch({
      type: USER_NOTIFICATION_FETCH_SUCCEEDED,
      payload: {
        ...notificationRes,
        offset,
        hasMore: offset + limit < notificationRes.totalCount
      }
    });
  } catch (e) {
    if (e.name === 'AbortError') return;
    dispatch(snackBarError({ message: 'Error in request' }));
    dispatch({ type: USER_NOTIFICATION_FETCH_FAILED, error: e });
    console.error(e);
  }
};

export const fetchUserNotificationCount = userId => async dispatch => {
  try {
    abortController.abort();

    dispatch({
      type: USER_NOTIFICATION_COUNT_FETCH_STARTED
    });

    const url = webPortalApi.buildApiUrl(`user/${userId}/notificationCount`);

    abortController = new AbortController();
    const { signal } = abortController;
    // Debounce to do not send multiple requests in short time
    await new Promise(res => {
      setTimeout(() => {
        res();
      }, 200);
    });
    const res = await fetch(url, { ...getRequestOptions(), signal });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }

    if (!res.ok) {
      dispatch(
        snackBarError({
          message: 'Error in request'
        })
      );
    }

    const notificationRes = await res.json();

    dispatch({
      type: USER_NOTIFICATION_COUNT_FETCH_SUCCEEDED,
      payload: notificationRes
    });
  } catch (e) {
    if (e.name === 'AbortError') return;
    dispatch(snackBarError({ message: 'Error in request' }));
    dispatch({ type: USER_NOTIFICATION_FETCH_FAILED, error: e });
    console.error(e);
  }
};

export const readUserNotification = (
  userId,
  notificationIds,
  readAll,
  isRead = 1
) => async dispatch => {
  try {
    abortController.abort();

    const url = webPortalApi.buildApiUrl(`user/${userId}/notification`);
    const payload = readAll ? { readAll: true } : { id: notificationIds };

    abortController = new AbortController();
    const { signal } = abortController;
    // Debounce to do not send multiple requests in short time
    await new Promise(res => {
      setTimeout(() => {
        res();
      }, 200);
    });

    const res = await fetch(url, {
      method: 'PATCH',
      body: JSON.stringify({ ...payload, isRead }),
      ...getRequestOptions(),
      signal
    });

    if (!res.ok) {
      throw await res.json();
    }

    const r = await res.json();

    dispatch(fetchUserNotificationCount(userId));

    dispatch({
      type: USER_NOTIFICATION_READ,
      payload: {
        ...payload,
        updatedCount: r.updatedCount,
        isRead
      }
    });

    return { success: true };
  } catch (e) {
    if (e.name === 'AbortError') return false;
    console.error(e);
    dispatch(snackBarError({ message: 'Error in request' }));
    return dispatch({ type: USER_NOTIFICATION_FETCH_FAILED, error: e });
  }
};

export const deleteUserNotification = (
  userId,
  notificationIds,
  deleteAll,
  withAutoUpdate = true
) => async dispatch => {
  try {
    abortController.abort();

    const url = webPortalApi.buildApiUrl(`user/${userId}/notification`);
    const payload = deleteAll ? { deleteAll: true } : { id: notificationIds };

    const res = await fetch(url, {
      method: 'DELETE',
      body: JSON.stringify(payload),
      ...getRequestOptions()
    });

    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    if (!res.ok) {
      throw await res.json();
    }

    await res.json();

    if (withAutoUpdate) {
      dispatch(fetchUserNotification(userId));
    }

    dispatch({
      type: USER_NOTIFICATION_DELETE,
      payload
    });

    return { success: true };
  } catch (e) {
    if (e.name === 'AbortError') return false;
    console.error(e);
    dispatch(snackBarError({ message: 'Error in request' }));
    return dispatch({ type: USER_NOTIFICATION_FETCH_FAILED, error: e });
  }
};

export const clearUserNotification = () => dispatch => {
  abortController.abort();
  dispatch({
    type: USER_NOTIFICATION_CLEAR
  });
};
